import React, { useState } from 'react';
import { useFormikContext } from 'formik';
import { observer } from 'mobx-react';
import { Grid, Button } from '@material-ui/core';
import { paymentMethod } from '@ourbranch/lookups';

import useSession from 'core/hooks/use-session';
import { LabelTooltip } from 'core/components/label-tooltip';
import { FormField } from 'core/components/form';
import { useStore } from 'core/store';
import addBankAccount from 'core/assets/svg/add-bank-account.svg';
import { useDisabledState } from 'common/disabled-context/context';
import { SecondaryPaymentMethod } from '../secondary-payment-method';
import { StripeCustomerIdField } from '../stripe-customer-id-field';
import { AddBankAccountModal } from './add-bank-account-modal';
import { useNormalizePaymentMethod } from '../hooks/use-normalize-payment-method';
import useStyles from '../billing-details-form.styles';

const BankAccounts = () => {
  const classes = useStyles();
  const { disabled } = useDisabledState();
  const { viewOnly } = useSession();
  const {
    account: {
      policies: {
        policy: { billingDetails }
      }
    }
  } = useStore();
  const { values } = useFormikContext();

  const allPaymentMethods = billingDetails.allPaymentMethods || [];
  const bankAccounts = allPaymentMethods
    .filter(
      (method) =>
        method.id?.startsWith('ba') ||
        method.type === 'us_bank_account' ||
        method.bankName ||
        method.basisTheoryBankAccountToken
    )
    .map((account) => ({
      id: account.id,
      value: account.last4.slice(-4).padStart(account.last4.length * 1.5, '*')
    }));

  const [isAddAccountModalOpen, setIsAddAccountModalOpen] = useState(false);

  useNormalizePaymentMethod(paymentMethod.ACH);

  return (
    <>
      <Grid
        container
        key="account-selected"
        justifyContent="flex-start"
        alignItems="center"
        className={classes.bankAccounts}
        spacing={4}
      >
        <Grid item xs={4}>
          <LabelTooltip
            label="Primary Account Number"
            tooltip={{
              label: 'More Info',
              onHoverText: 'This is the primary bank account we’ll use to process payments for this policy.'
            }}
          >
            <FormField
              name="defaultBankAccount.id"
              type="select"
              mode="dark"
              options={bankAccounts}
              permissions={{ isLicensedAction: false }}
              fast={false}
            />
          </LabelTooltip>
        </Grid>

        <StripeCustomerIdField />

        <Button
          onClick={() => setIsAddAccountModalOpen(true)}
          variant="contained"
          color="secondary"
          className={classes.addCardButton}
          disabled={disabled}
        >
          <img src={addBankAccount} alt="Add account" className={classes.addCardImage} />
          Add new account
        </Button>
      </Grid>
      {isAddAccountModalOpen && (
        <AddBankAccountModal open={isAddAccountModalOpen} onClose={() => setIsAddAccountModalOpen(false)} />
      )}
      {values.defaultBankAccount?.accountHolder && (
        <Grid container className={classes.bankAccounts} spacing={4}>
          <FormField
            name="defaultBankAccount.accountHolder"
            type="value"
            label="Account Holder"
            mode="dark"
            xs={4}
            value={values.defaultBankAccount.accountHolder}
          />
          <FormField
            name="defaultBankAccount.routingNumber"
            type="value"
            label="Routing Number"
            xs={4}
            mode="dark"
            value={values.defaultBankAccount.routingNumber}
          />
        </Grid>
      )}
      <Grid container className={classes.bankAccounts} spacing={4}>
        {!viewOnly && <SecondaryPaymentMethod />}
      </Grid>
    </>
  );
};

export default observer(BankAccounts);
